import React, { useState, useEffect } from 'react';
import { Send, Upload, Users, Settings, MessageSquare, Radio, CheckCircle, XCircle, RefreshCw } from 'lucide-react';

declare global {
  interface Window {
    fbAsyncInit?: () => void;
    FB?: any;
  }
}

interface FBLoginStatusResponse {
  status: 'connected' | 'not_authorized' | 'unknown';
  authResponse?: {
    accessToken: string;
  };
}

const API_BASE = 'https://spotlink.pinnaclegraphics08.workers.dev';
const API_KEY = '865786';

export default function WhatsAppDashboard() {
  const [fbUser, setFbUser] = useState<any>(null);
  const [fbInitialized, setFbInitialized] = useState(false); // Track SDK initialization
  const [activeTab, setActiveTab] = useState('settings');
  const [user, setUser] = useState<any>(null);
  const [conversations, setConversations] = useState<any[]>([]);
  const [selectedConvo, setSelectedConvo] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [selectedContacts, setSelectedContacts] = useState<any[]>([]);
  const [csvContacts, setCsvContacts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [wabaLinked, setWabaLinked] = useState(false);
  const [embeddedSignupVisible, setEmbeddedSignupVisible] = useState(false);

  useEffect(() => {
    window.fbAsyncInit = function() {
      window.FB.init({
        appId      : '2294386157677689', // REPLACE WITH YOUR ACTUAL APP ID
        cookie     : true,
        xfbml      : true,
        version    : 'v19.0'
      });
      
      setFbInitialized(true); // Mark SDK as initialized
      
      window.FB.getLoginStatus(function(response: FBLoginStatusResponse) {
        statusChangeCallback(response);
      });
    };

    (function(d, s, id){
      var js: HTMLScriptElement, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) {return;}
      js = d.createElement(s) as HTMLScriptElement;
      js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      fjs.parentNode?.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));
  }, []);

  function statusChangeCallback(response: any) {
    if (response.status === 'connected') {
      window.FB.api('/me', function(userInfo: any) {
        setFbUser({ ...userInfo, accessToken: response.authResponse.accessToken });
      });
    } else {
      setFbUser(null);
    }
  }

  const handleFbLogin = () => {
    if (!fbInitialized || !window.FB) {
      alert('Facebook SDK is still loading. Please wait a moment and try again.');
      return;
    }
    window.FB.login(statusChangeCallback, {scope: 'email,public_profile'});
  };

  const userId = fbUser ? fbUser.id : null;

  useEffect(() => {
    if (userId) {
      loadUserSettings();
      loadConversations();
    }
  }, [userId]);

  const loadUserSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/users/${userId}`, {
        headers: { 'X-API-Key': API_KEY }
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setWabaLinked(!!data.waba_id);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadConversations = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/conversations/${userId}`, {
        headers: { 'X-API-Key': API_KEY }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      setConversations([]);
    }
  };

  const loadMessages = async (convoId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/conversations/${userId}/${convoId}/messages`, {
        headers: { 'X-API-Key': API_KEY }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      setMessages([]);
    }
  };

  const selectConversation = (convo: any) => {
    setSelectedConvo(convo);
    loadMessages(convo.id);
  };

  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedConvo) return;
    setLoading(true);
    try {
      await fetch(`${API_BASE}/api/conversations/${userId}/${selectedConvo.id}/messages`, {
        method: 'POST',
        headers: { 'X-API-Key': API_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: messageInput })
      });
      setMessageInput('');
      loadMessages(selectedConvo.id);
    } catch (error) {
      console.error('Error sending:', error);
    }
    setLoading(false);
  };

  const updateSettings = async (updates: any) => {
    setLoading(true);
    try {
      await fetch(`${API_BASE}/api/users/${userId}`, {
        method: 'PUT',
        headers: { 'X-API-Key': API_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      loadUserSettings();
    } catch (error) {
    }
    setLoading(false);
  };

  const showEmbeddedSignup = () => {
    setEmbeddedSignupVisible(true);
  };

  const handleWabaManualLink = async (formData: any) => {
    setLoading(true);
    try {
      await fetch(`${API_BASE}/api/users/${userId}/waba-link`, {
        method: 'POST',
        headers: { 'X-API-Key': API_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      setWabaLinked(true);
      setEmbeddedSignupVisible(false);
      loadUserSettings();
    } catch (error) {
    }
    setLoading(false);
  };

  const handleCSVUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event: any) => {
      const text = event.target.result;
      const lines = text.split('\n').filter((line: string) => line.trim());
      const contacts = lines.slice(1).map((line: string, idx: number) => {
        const [name, phone] = line.split(',').map((s: string) => s.trim());
        return { id: `csv_${idx}`, name, phone };
      });
      setCsvContacts(contacts);
    };
    reader.readAsText(file);
  };

  const toggleContactSelection = (contact: any) => {
    setSelectedContacts(prev => 
      prev.find(c => c.id === contact.id)
        ? prev.filter(c => c.id !== contact.id)
        : [...prev, contact]
    );
  };

  const sendBroadcast = async () => {
    if (!broadcastMessage.trim() || selectedContacts.length === 0) {
      alert('Please enter a message and select contacts');
      return;
    }
    setLoading(true);
    try {
      await fetch(`${API_BASE}/api/users/${userId}/broadcast`, {
        method: 'POST',
        headers: { 'X-API-Key': API_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: broadcastMessage, contacts: selectedContacts })
      });
      setBroadcastMessage('');
      setSelectedContacts([]);
      setCsvContacts([]);
    } catch (error) {
    }
    setLoading(false);
  };

  if (!fbUser) return (
    <div style={{padding:'20px',textAlign:'center'}}>
      <h1>WhatsApp Dashboard</h1>
      <p>Login with Facebook to continue</p>
      <button onClick={handleFbLogin} disabled={!fbInitialized}>
        {fbInitialized ? 'Login with Facebook' : 'Loading Facebook SDK...'}
      </button>
    </div>
  );

  return (
    <div style={{display:'flex',height:'100vh',fontFamily:'sans-serif'}}>
      <div style={{width:'200px',background:'#f4f4f4',padding:'10px',borderRight:'1px solid #ccc'}}>
        <h2>Dashboard</h2>
        <button onClick={()=>setActiveTab('messages')} style={{display:'block',width:'100%',padding:'10px',marginBottom:'5px'}}>
          <MessageSquare size={18}/> Messages
        </button>
        <button onClick={()=>setActiveTab('broadcast')} style={{display:'block',width:'100%',padding:'10px',marginBottom:'5px'}}>
          <Radio size={18}/> Broadcast
        </button>
        <button onClick={()=>setActiveTab('settings')} style={{display:'block',width:'100%',padding:'10px',marginBottom:'5px'}}>
          <Settings size={18}/> Settings
        </button>
      </div>

      <div style={{flex:1,padding:'20px',overflowY:'auto'}}>
        {activeTab === 'settings' && (
          <div>
            <h2>Settings</h2>
            <p>Welcome, {fbUser.name}</p>
            <p>WABA Status: {wabaLinked ? "Linked" : "Not Linked"}</p>
            {!wabaLinked ? (
              <div>
                <button onClick={showEmbeddedSignup}>Link WhatsApp Business Account (Embedded Signup)</button>
              </div>
            ) : (
              <div>
                <p>Your webhook:</p>
                <code>{user?.webhook_url}</code>
              </div>
            )}
          </div>
        )}

        {activeTab === 'messages' && (
          <div style={{display:'flex',height:'100%'}}>
            <div style={{width:'250px',borderRight:'1px solid #ccc',padding:'10px',overflowY:'auto'}}>
              <h3>Conversations</h3>
              {conversations.map((c: any) => (
                <div key={c.id} onClick={()=>selectConversation(c)} style={{padding:'10px',cursor:'pointer',background:selectedConvo?.id===c.id?'#e0e0e0':'#fff'}}>
                  {c.name || c.phone}
                </div>
              ))}
            </div>
            <div style={{flex:1,display:'flex',flexDirection:'column',padding:'10px'}}>
              {selectedConvo ? (
                <>
                  <h3>{selectedConvo.name || selectedConvo.phone}</h3>
                  <div style={{flex:1,overflowY:'auto',border:'1px solid #ccc',padding:'10px',marginBottom:'10px'}}>
                    {messages.map((m: any, i: number) => (
                      <div key={i} style={{marginBottom:'10px'}}>
                        <strong>{m.from === 'user' ? 'You' : selectedConvo.name}:</strong> {m.text}
                      </div>
                    ))}
                  </div>
                  <div style={{display:'flex'}}>
                    <input 
                      type="text" 
                      value={messageInput} 
                      onChange={(e)=>setMessageInput(e.target.value)}
                      style={{flex:1,padding:'10px',marginRight:'5px'}}
                      placeholder="Type a message"
                    />
                    <button onClick={sendMessage} disabled={loading}>
                      <Send size={18}/> Send
                    </button>
                  </div>
                </>
              ) : (
                <p>Select a conversation</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'broadcast' && (
          <div>
            <h2>Broadcast Message</h2>
            <input type="file" accept=".csv" onChange={handleCSVUpload} />
            {csvContacts.length > 0 && (
              <div>
                <h3>Contacts ({csvContacts.length})</h3>
                {csvContacts.map((c: any) => (
                  <div key={c.id}>
                    <input 
                      type="checkbox" 
                      checked={!!selectedContacts.find(sc => sc.id === c.id)}
                      onChange={()=>toggleContactSelection(c)}
                    />
                    {c.name} - {c.phone}
                  </div>
                ))}
                <textarea 
                  value={broadcastMessage} 
                  onChange={(e)=>setBroadcastMessage(e.target.value)}
                  placeholder="Enter broadcast message"
                  rows={4}
                  style={{width:'100%',marginTop:'10px'}}
                />
                <button onClick={sendBroadcast} disabled={loading}>Send Broadcast</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
